import { useCallback, useEffect, useRef, useState } from 'react';
import { ConditionWord } from '../inventory/ConditionWord';
import { OemWord } from '../inventory/OemWord';
import { useMounted } from '../ui/useMounted';
import {
  countPositions,
  findOpenSession,
  forgetSession,
  loadLocal,
  NO_CELL_ID,
  openSession,
  rememberNote,
  rememberUnlistedLine,
  resolvePartScan,
  statusOf,
} from '../inventory/inventory';
import type {
  InventoryLine,
  InventorySession,
  LineStatus,
  LocalCount,
  LocalNote,
  WarehouseCode,
} from '../inventory/inventory';
import type { OutboxRecord } from '../outbox/outbox';
import { rememberCount } from '../inventory/inventory';
import { ApiError } from '../api/client';
import type { Reference } from '../reference/reference';
import { resolveScan } from '../scan/codes';
import { ScanOverlay } from '../scan/ScanOverlay';

/**
 * Пересчёт склада с телефона — одной полки или всего склада.
 *
 * <p>Работа идёт по ячейкам: открывая пересчёт, кладовщик выбирает не только
 * склад, но и ячейку — это и даёт весь выигрыш от телефона. Документ
 * на тридцать три тысячи позиций никто не закрывает: обход идёт неделями,
 * а без «Завершить подсчёт» нельзя провести расхождения. Одна полка —
 * это минуты.
 *
 * <p><b>Пустое поле — это «не дошли», а не «не нашли».</b> Разница в том, что
 * непосчитанное не проводится вовсе, а посчитанный ноль — это недостача,
 * которую спишут. Поэтому ноль вносится отдельной кнопкой, а не остаётся
 * значением по умолчанию: подмена одного другим списывает полсклада.
 *
 * <p><b>Скан детали прибавляет единицу, а не выставляет факт равным учёту.</b>
 * На разборке товар в основном штучный, но колёса и метизы бывают парами,
 * и «сканирую столько раз, сколько штук нашёл» — единственное правило,
 * которое одинаково работает и там, и там.
 */
interface Props {
  reference: Reference;
  onCount(sessionId: number, line: InventoryLine, qty: string, countedAt: number): void;
  /**
   * Ставит комментарий ходившего в очередь отправки (задача 0169).
   *
   * <p>Не передан — роль комментарий не пишет (продавец считает, но поле
   * ему не положено), и поля на экране нет вовсе: поле, которое сервер
   * отобьёт, хуже отсутствующего.
   */
  onNote?: ((sessionId: number, text: string) => void) | undefined;
  /**
   * Записи очереди с комментариями — экран говорит, ждёт ли написанное
   * отправки и не отбил ли его сервер. Без них он молчал бы о том, что
   * комментарий ещё на телефоне.
   */
  noteQueue?: OutboxRecord[] | undefined;
}

type Tab = 'all' | 'unscanned' | 'problem' | 'scanned';

const TABS: { key: Tab; label: string }[] = [
  { key: 'all', label: 'Все' },
  { key: 'unscanned', label: 'Не сканировались' },
  { key: 'problem', label: 'С проблемами' },
  { key: 'scanned', label: 'Отсканированы' },
];

export function InventoryScreen({ reference, onCount, onNote, noteQueue = [] }: Props) {
  const [warehouseId, setWarehouseId] = useState('');
  /**
   * Выборка формы открытия: '' — «Любая», 'none' — «Без адреса», иначе
   * строковый id ячейки. Отдельно от состояния уже открытой сессии — там
   * своей выборки для навигации больше нет, её заменили вкладки по статусу.
   */
  const [scopeCell, setScopeCell] = useState('');
  const [found, setFound] = useState<
    { status: 'idle' } | { status: 'loading' } | { status: 'ok'; value: number }
    | { status: 'error'; message: string }
  >({ status: 'idle' });

  const [session, setSession] = useState<InventorySession | null>(null);
  const [lines, setLines] = useState<InventoryLine[]>([]);
  const [counts, setCounts] = useState<Record<string, LocalCount>>({});
  const [codes, setCodes] = useState<WarehouseCode[]>([]);
  const [tab, setTab] = useState<Tab>('all');
  const [scanning, setScanning] = useState<'cell' | 'part' | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Комментарий ходившего: записанный на телефоне и набираемый. Черновик
  // отдельно — кнопка гаснет ровно тогда, когда отправлять нечего.
  const [localComment, setLocalComment] = useState<LocalNote | null>(null);
  const [commentDraft, setCommentDraft] = useState('');

  // Экран уходит с вкладки не дожидаясь ответа IndexedDB или сервера —
  // офлайн-хранилище и счётчик формы отвечают не мгновенно, — и без сторожа
  // асинхронный колбэк правит состояние уже размонтированного компонента.
  // Почему это общий хук, а не ref с эффектом на месте, — в ui/useMounted.ts.
  const mounted = useMounted();

  const reload = useCallback(async () => {
    const local = await loadLocal();
    if (!mounted.current) {
      return;
    }
    setSession(local.session);
    setLines(local.lines);
    setCounts(local.counts);
    setCodes(local.codes);
    setLocalComment(local.note);
    // Записанное на этом телефоне свежее скачанного при открытии: лист
    // обхода берёт комментарий сервера один раз, а очередь могла уже
    // отправить новый.
    setCommentDraft(local.note?.text ?? local.session?.counterNote ?? '');
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Счётчик «Найдено товаров» пересчитывается при каждой смене склада или
  // ячейки — сторож на случай, если ответ придёт не в том порядке, в котором
  // ушёл запрос.
  const requestId = useRef(0);
  useEffect(() => {
    if (warehouseId === '') {
      setFound({ status: 'idle' });
      return;
    }
    const id = ++requestId.current;
    setFound({ status: 'loading' });
    void countPositions(Number(warehouseId), resolveCellId(scopeCell))
      .then((value) => {
        if (mounted.current && requestId.current === id) {
          setFound({ status: 'ok', value });
        }
      })
      .catch((cause: unknown) => {
        if (mounted.current && requestId.current === id) {
          setFound({
            status: 'error',
            message: cause instanceof ApiError && cause.message !== ''
              ? cause.message
              : 'Не удалось посчитать',
          });
        }
      });
  }, [warehouseId, scopeCell]);

  if (session === null) {
    const cells = reference.warehouses.find((w) => w.id === Number(warehouseId))?.cells ?? [];

    return (
      <section className="card">
        <h2>Инвентаризация</h2>
        <p className="note">
          Лист обхода скачивается целиком при открытии — сделайте это там, где есть связь.
          В ангаре пересчёт работает без неё, но начать без связи нельзя.
        </p>

        <label>
          Склад
          {/* Без умолчания: пересчёт не того склада — это обход, который
              кладовщик сделает целиком и который спишет недостачу там,
              где ничего не считали. Какой склад считают, знает он. */}
          <select
            value={warehouseId}
            onChange={(e) => {
              setWarehouseId(e.target.value);
              setScopeCell('');
              setNote(null);
            }}
          >
            <option value="">— выберите склад —</option>
            {reference.warehouses.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          Ячейка
          <select
            value={scopeCell}
            disabled={warehouseId === ''}
            onChange={(e) => { setScopeCell(e.target.value); setNote(null); }}
          >
            <option value="">Любая</option>
            {cells.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.code}</option>
            ))}
            <option value="none">Без адреса</option>
          </select>
        </label>

        {warehouseId !== '' && (
          <p className="note">
            {found.status === 'loading' && 'Загружаем…'}
            {found.status === 'error' && found.message}
            {found.status === 'ok' && `Найдено товаров: ${found.value}`}
          </p>
        )}

        <button type="button" disabled={busy || warehouseId === ''}
                onClick={() => void start(false)}>
          Продолжить начатую
        </button>
        <button type="button" disabled={busy || warehouseId === ''}
                onClick={() => void start(true)}>
          Открыть новую
        </button>
        {note !== null && <p className="note">{note}</p>}
      </section>
    );
  }

  const groups = groupBy(lines, counts);
  const shown = groups[tab === 'all' ? 'unscanned' : tab];
  const savedComment = localComment?.text ?? session.counterNote ?? '';
  const commentState = commentDelivery(noteQueue, session.id);

  return (
    <section className="card">
      <h2>Инвентаризация</h2>
      <p className="note">
        Сессия {session.id} · посчитано {session.counted} из {lines.length}
      </p>

      <div className="row">
        <button type="button" onClick={() => setScanning('cell')}>
          Сканировать ячейку
        </button>
        <button type="button" onClick={() => setScanning('part')}>
          Сканировать деталь
        </button>
      </div>

      {note !== null && <p className="note">{note}</p>}

      <div className="tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={tab === t.key ? 'tab tab--active' : 'tab'}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'all' ? (
        <>
          <h4>Не сканировались</h4>
          <CountGroup lines={groups.unscanned} status="unscanned" counts={counts} onSubmit={submit} />
          <h4>С проблемами</h4>
          <CountGroup lines={groups.problem} status="problem" counts={counts} onSubmit={submit} />
          <h4>Отсканированы</h4>
          <CountGroup lines={groups.scanned} status="scanned" counts={counts} onSubmit={submit} />
        </>
      ) : (
        <CountGroup lines={shown} status={tab} counts={counts} onSubmit={submit} />
      )}

      {/*
        * Комментарий ходившего — в конце обхода, один на документ (задача
        * 0169, решение владельца продукта от 29 сентября 2026), а не у каждой
        * полки: пополочный означал бы операцию очереди на каждую строку.
        *
        * Уходит очередью, а не запросом: обход работает без связи. Экран
        * не говорит «сохранено» — пока запись в очереди, это неправда, —
        * а называет, что она ждёт отправки или чем её отбил сервер.
        */}
      {onNote !== undefined && (
        <>
          <hr />
          <div className="field">
            <label htmlFor="counter-note">Комментарий</label>
            <textarea
              id="counter-note"
              rows={2}
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
            />
            <div className="row">
              <button
                type="button"
                disabled={commentDraft.trim() === '' || commentDraft.trim() === savedComment}
                onClick={() => void saveComment()}
              >
                Сохранить комментарий
              </button>
            </div>
            {commentState !== null && (
              <p className={commentState.failed ? 'note note--error' : 'note'}>
                {commentState.text}
              </p>
            )}
          </div>
        </>
      )}

      <hr />
      <button
        type="button"
        className="button--ghost"
        onClick={() => {
          // Локальный лист убираем, сессию на сервере не трогаем: завершает
          // её тот, кто сводит расхождения, и делает это не с телефона.
          void forgetSession().then(reload);
        }}
      >
        Убрать лист с телефона
      </button>

      {scanning !== null && (
        <ScanOverlay
          hint={scanning === 'cell' ? 'Код ячейки' : 'Штрихкод детали'}
          onScan={scanning === 'cell' ? applyCellScan : applyPartScan}
          onClose={() => setScanning(null)}
        />
      )}
    </section>
  );

  /**
   * Записывает комментарий на телефоне и ставит его в очередь.
   *
   * <p>Очередь — раньше проверки «экран ещё на месте»: кладовщик, ушедший
   * с вкладки сразу после нажатия, не должен терять написанное.
   */
  async function saveComment(): Promise<void> {
    if (session === null || onNote === undefined) {
      return;
    }
    const text = commentDraft.trim();
    if (text === '') {
      return;
    }
    const saved = await rememberNote(session.id, text);
    onNote(session.id, text);
    if (mounted.current) {
      setLocalComment(saved);
      setCommentDraft(text);
    }
  }

  async function start(fresh: boolean): Promise<void> {
    setBusy(true);
    setNote(null);
    try {
      // Ни выбора, ни подстановки: без склада пересчёт не начинается вовсе.
      // Кнопки до выбора погашены, так что сюда попадают только с выбранным;
      // проверка остаётся сторожем на случай, если кнопку разблокируют.
      if (warehouseId === '') {
        setNote('Выберите склад: пересчёт идёт по одному складу, и какой — знаете вы');
        return;
      }

      const cellId = resolveCellId(scopeCell);
      const opened = fresh
        ? await openSession(Number(warehouseId), cellId)
        : await findOpenSession(Number(warehouseId), cellId);

      if (opened === null) {
        // Тот же сторож, что ниже: ответ сервера приходит после того,
        // как кладовщик мог уйти с вкладки.
        if (mounted.current) {
          setNote('На этом складе инвентаризация не открыта');
        }
        return;
      }
      await reload();
    } catch (error) {
      if (mounted.current) {
        setNote(error instanceof Error ? error.message : 'Не удалось открыть');
      }
    } finally {
      // `finally` выполняется и на том пути, где кладовщик ушёл с вкладки,
      // пока сервер отвечал, — поэтому сторож нужен и здесь.
      if (mounted.current) {
        setBusy(false);
      }
    }
  }

  async function submit(line: InventoryLine, qty: string): Promise<void> {
    if (session === null) {
      return;
    }
    // Сначала локально: экран обязан показать пройденную полку немедленно,
    // не дожидаясь связи, которой в ангаре нет.
    const count = await rememberCount(line.partId, qty);
    if (!mounted.current) {
      return;
    }
    setCounts((prev) => ({ ...prev, [String(line.partId)]: count }));
    onCount(session.id, line, qty, count.countedAt);
  }

  /** Скан прибавляет к факту единицу — сколько раз нашли, столько и посчитали. */
  async function scanLine(line: InventoryLine, noteText: string): Promise<void> {
    const current = counts[String(line.partId)];
    const next = String((current === undefined ? 0 : Number(current.qty)) + 1);
    await submit(line, next);
    if (mounted.current) {
      setNote(noteText);
    }
  }

  function applyCellScan(text: string): void {
    setScanning(null);
    const match = resolveScan(reference, session?.warehouseId ?? null, text);
    if (match.kind === 'cell') {
      setNote(`Ячейка ${match.cell.code}`);
      return;
    }
    setNote(`Код «${text}» не похож на ячейку этого склада`);
  }

  function applyPartScan(text: string): void {
    setScanning(null);
    const match = resolvePartScan(codes, lines, text);

    if (match.kind === 'not-found') {
      setNote(`Код «${text}» не найден на этом складе`);
      return;
    }
    if (match.kind === 'listed') {
      const line = lines.find((l) => l.partId === match.partId);
      if (line !== undefined) {
        void scanLine(line, 'Отсканирован');
      }
      return;
    }

    // Деталь есть на складе, но не в текущем листе обхода — «вне списка»,
    // а не «неизвестно»: она лежит не на своей полке, и это надо увидеть,
    // а не потерять.
    const newLine: InventoryLine = {
      partId: match.code.partId,
      number: match.code.number,
      title: match.code.title,
      condition: match.code.condition ?? null,
      primaryOem: match.code.primaryOem ?? null,
      cellId: match.code.cellId,
      cellCode: match.code.cellCode,
      qtyExpected: '0',
      qtyCounted: null,
    };
    void rememberUnlistedLine(newLine).then(() => {
      if (!mounted.current) {
        return;
      }
      setLines((prev) => (prev.some((l) => l.partId === newLine.partId) ? prev : [...prev, newLine]));
      void scanLine(newLine, 'Вне списка');
    });
  }
}

/**
 * Что с комментарием этой сессии в очереди: ждёт отправки или отбит.
 *
 * <p>Нет записи — экран молчит, а не говорит «отправлен»: пустой список
 * бывает и тогда, когда очередь ещё не прочитана, и подтверждать доставку
 * по нему значило бы обещать то, чего не знаем.
 */
function commentDelivery(
  queue: OutboxRecord[],
  sessionId: number,
): { text: string; failed: boolean } | null {
  const mine = queue.filter((record) =>
    record.kind === 'inventoryNote'
      && (record.payload as { sessionId?: number } | null)?.sessionId === sessionId);
  const latest = mine[mine.length - 1];
  if (latest === undefined) {
    return null;
  }
  if (latest.state === 'failed') {
    return { text: `Не принят: ${latest.lastError ?? 'сервер отказал'}`, failed: true };
  }
  return { text: 'Ждёт отправки — уйдёт, когда будет связь', failed: false };
}

/** Разбирает выбор ячейки формы открытия в то, что ждёт сервер. */
function resolveCellId(scopeCell: string): number | undefined {
  if (scopeCell === '') {
    return undefined;
  }
  return scopeCell === 'none' ? NO_CELL_ID : Number(scopeCell);
}

function groupBy(
  lines: InventoryLine[],
  counts: Record<string, LocalCount>,
): Record<LineStatus, InventoryLine[]> {
  const groups: Record<LineStatus, InventoryLine[]> = { unscanned: [], problem: [], scanned: [] };
  for (const line of lines) {
    groups[statusOf(line, counts)].push(line);
  }
  return groups;
}

function CountGroup({
  lines,
  status,
  counts,
  onSubmit,
}: {
  lines: InventoryLine[];
  status: LineStatus;
  counts: Record<string, LocalCount>;
  onSubmit: (line: InventoryLine, qty: string) => void;
}) {
  if (lines.length === 0) {
    return <p className="muted">Ничего не найдено</p>;
  }
  return (
    <ul className="count-list">
      {lines.map((line) => (
        <CountRow
          key={line.partId}
          line={line}
          status={status}
          count={counts[String(line.partId)]}
          onSubmit={(qty) => onSubmit(line, qty)}
        />
      ))}
    </ul>
  );
}

/**
 * Строка полки.
 *
 * <p>Учётное количество показывается: скрывать его — популярный приём против
 * подгонки, но на разборке пересчитывают штучный товар, где подгонять нечего,
 * а слепой ввод даёт опечатки, которые потом проводятся как расхождения.
 */
function CountRow({
  line,
  status,
  count,
  onSubmit,
}: {
  line: InventoryLine;
  status: LineStatus;
  count: LocalCount | undefined;
  onSubmit: (qty: string) => void;
}) {
  const [value, setValue] = useState('');

  return (
    <li className={status === 'scanned' ? 'count-row count-row--done' : 'count-row'}>
      <div className="count-title">
        {line.title}
        {/* Состояние (задача 0318): на полке ищут конкретную деталь, а «Фара…»
            новая и б/у лежат рядом. Пометку в наименовании, которая говорила
            это до сих пор, снимают (0315, 0316). */}
        <ConditionWord code={line.condition} />
        <OemWord number={line.primaryOem} />
        {/* Основной номер производителя (задача 0329): две «Фары Toyota Camry
            · б/у» на одной полке различает только он. */}
        {/* Номер позиции (задача 0168): найдя на полке не то, кладовщик
            называет деталь вслух тому, кто сводит расхождения, — а публичного
            кода в листе обхода нет вовсе, и «Фара Toyota Camry» на складе
            не одна. */}
        <span className="muted"> · №&nbsp;{line.number}</span>
        {status !== 'problem' && <span className="muted"> · учёт {line.qtyExpected}</span>}
        {count !== undefined && <span className="muted"> · посчитано {count.qty}</span>}
        {' '}
        <StatusBadge status={status} />
      </div>
      <div className="row">
        <input
          type="number"
          inputMode="numeric"
          min="0"
          value={value}
          placeholder="факт"
          onChange={(e) => setValue(e.target.value)}
        />
        <button
          type="button"
          disabled={value.trim() === ''}
          onClick={() => {
            onSubmit(value.trim());
            setValue('');
          }}
        >
          Внести
        </button>
        <button
          type="button"
          className="button--ghost"
          // Отдельной кнопкой намеренно: посчитанный ноль — это недостача,
          // и она должна вноситься осознанным действием, а не пустым полем.
          onClick={() => onSubmit('0')}
        >
          Нет на полке
        </button>
      </div>
    </li>
  );
}

function StatusBadge({ status }: { status: LineStatus }) {
  if (status === 'scanned') {
    return <span className="badge badge--online">Отсканирован</span>;
  }
  if (status === 'problem') {
    return <span className="badge badge--offline">Вне списка</span>;
  }
  return <span className="badge badge--muted">Не сканирован</span>;
}
